/**
 * Orbit voice I/O — Whisper transcribe + gpt-4o-mini-tts streaming playback.
 *
 * `transcribeAudio(blob)`  → text
 * `speakText(text, opts)`  → plays audio through the browser, returns a handle
 *                            with `stop()` + `done` Promise.
 */
import { getApiBaseUrl } from './api';

// ── Transcribe ────────────────────────────────────────────────────────

export interface TranscribeResult {
  text: string;
  language?: string;
  durationSeconds?: number;
}

export async function transcribeAudio(
  blob: Blob,
  opts?: { language?: string; prompt?: string; signal?: AbortSignal }
): Promise<TranscribeResult> {
  if (!blob || blob.size === 0) {
    throw new Error('Empty audio payload');
  }

  const form = new FormData();
  const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'mp4' : 'webm';
  form.append('audio', blob, `orbit-${Date.now()}.${ext}`);
  if (opts?.language) form.append('language', opts.language);
  if (opts?.prompt) form.append('prompt', opts.prompt);

  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${getApiBaseUrl()}/voice/transcribe`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: form,
    signal: opts?.signal,
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Transcribe failed (${response.status}): ${detail}`);
  }

  const json = await response.json();
  return {
    text: (json.text || '').trim(),
    language: json.language,
    durationSeconds: json.duration_seconds,
  };
}

// ── Speak ─────────────────────────────────────────────────────────────

export type OrbitVoice = 'alloy' | 'ash' | 'ballad' | 'coral' | 'echo' | 'fable' | 'onyx' | 'nova' | 'sage' | 'shimmer' | 'verse';

export interface SpeakOptions {
  voice?: OrbitVoice;
  instructions?: string;
  speed?: number;
  onStart?: () => void;
  onEnd?: () => void;
  signal?: AbortSignal;
}

export interface SpeakHandle {
  stop: () => void;
  done: Promise<void>;
  audio: HTMLAudioElement;
}

export function speakText(text: string, opts: SpeakOptions = {}): SpeakHandle {
  const controller = new AbortController();
  const signal = opts.signal ?? controller.signal;
  const audio = new Audio();
  audio.preload = 'auto';

  let objectUrl: string | null = null;
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    try { audio.pause(); } catch { /* noop */ }
    try { controller.abort(); } catch { /* noop */ }
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  };

  const done = (async () => {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${getApiBaseUrl()}/voice/speak`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        text,
        voice: opts.voice ?? 'nova',
        instructions: opts.instructions,
        speed: opts.speed ?? 1.0,
      }),
      signal,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(`TTS failed (${response.status}): ${detail}`);
    }

    // Buffer-then-play is simplest and reliably low-latency for short utterances.
    // (Fully streaming MSE playback buys ~150ms at the cost of major complexity.)
    const blob = await response.blob();
    if (stopped) return;

    // A streamed TTS error lands as a 200 with an empty body (the server can't
    // change status after headers flush). Treat empty audio as a failure so the
    // caller's catch surfaces it instead of silently "playing" nothing.
    if (blob.size === 0) {
      throw new Error('TTS returned empty audio');
    }

    objectUrl = URL.createObjectURL(blob);
    audio.src = objectUrl;
    audio.onplay = () => opts.onStart?.();

    try {
      await audio.play();
    } catch (err) {
      // Browser autoplay policies sometimes block silent playback; surface cleanly.
      throw err;
    }

    await new Promise<void>((resolve) => {
      audio.onended = () => resolve();
      audio.onerror = () => resolve();
    });

    opts.onEnd?.();
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
  })();

  return { stop, done, audio };
}
